'use client';
import { useState, useEffect, useRef, useCallback } from 'react';

interface UseCountUpOptions {
  end: number;
  start?: number;
  duration?: number;
  enabled?: boolean;
  prefix?: string;
  suffix?: string;
  separator?: string;
}

/**
 * Animates a number from start to end when enabled.
 * Uses requestAnimationFrame for smooth 60fps animation.
 * Respects prefers-reduced-motion — snaps to final value immediately.
 */
export function useCountUp({
  end,
  start = 0,
  duration = 1800,
  enabled = false,
  prefix = '',
  suffix = '',
  separator = ',',
}: UseCountUpOptions) {
  const [value, setValue] = useState(start);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatNumber = useCallback(
    (n: number) => {
      const rounded = Math.round(n);
      const formatted = separator
        ? rounded.toLocaleString('en-IN')
        : String(rounded);
      return `${prefix}${formatted}${suffix}`;
    },
    [prefix, suffix, separator],
  );

  useEffect(() => {
    if (!enabled) {
      setValue(start);
      return;
    }

    if (prefersReducedMotion) {
      setValue(end);
      return;
    }

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic for natural deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + (end - start) * eased;

      setValue(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    startTimeRef.current = 0;
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, end, start, duration, prefersReducedMotion]);

  return { value: Math.round(value), formatted: formatNumber(value) };
}
